import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { axiosInstance } from '@/lib/axios'

export interface ServerCompanyModel {
  /** Company configuration entity id (GUID from API). */
  id: string
  name: string
  uniqueIdentifier: string
  databaseName: string | null
  databaseVersion: string | null
  isActive: boolean
  isServiceCompany: boolean
  accountingOnly: boolean
  created: string | null
}

export interface ServerListModel {
  id: number
  name: string
  address: string
  login: string | null
  companies: number
  databases: number
  status: string | null
  isPrivate: boolean
  isDefault?: boolean
  inUse: boolean
}

export interface WpfCompanyModel {
  name: string
  uniqueIdentifier: string
  inUse: boolean
}

export function getServerCompanies(serverId: number) {
  return axiosInstance.get<ServerCompanyModel[]>(
    `/config-api/server/companies?serverId=${serverId}`,
  )
}

export function useQueryServerCompanies(serverId: number | null) {
  return useQuery({
    queryKey: ['server-companies', serverId],
    queryFn: async () => {
      const { data } = await getServerCompanies(serverId!)
      return data
    },
    enabled: serverId != null,
  })
}

export function getAllServers() {
  return axiosInstance.get<ServerListModel[]>('/config-api/server')
}

export function useQueryAllServers(enabled: boolean) {
  return useQuery({
    queryKey: ['all-servers'],
    queryFn: async () => {
      const { data } = await getAllServers()
      return data
    },
    enabled,
  })
}

export function getWpfCompaniesOnServer(serverId: number) {
  return axiosInstance.get<WpfCompanyModel[]>(
    `/api/${serverId}/wpfcompany?serverId=${serverId}`,
  )
}

export function useQueryWpfCompaniesOnServer(serverId: number | null) {
  return useQuery({
    queryKey: ['wpf-companies', serverId],
    queryFn: async () => {
      const { data } = await getWpfCompaniesOnServer(serverId!)
      return data
    },
    enabled: serverId != null,
  })
}

export interface CreateCompanyOnServerRequest {
  tenantId: string
  serverId: number
  companyName: string
  uniqueIdentifier: string
  nip?: string
  regon?: string
  krs?: string
  email?: string
  notificationsEmail?: string
  wpfCompanies?: WpfCompanyModel[]
}

export function createCompanyOnServer(request: CreateCompanyOnServerRequest) {
  const payload = {
    tenantId: request.tenantId,
    companyName: request.companyName,
    uniqueIdentifier: request.uniqueIdentifier,
    nip: request.nip,
    regon: request.regon,
    krs: request.krs,
    email: request.email,
    notificationsEmail: request.notificationsEmail,
    legacy: true,
    isServiceCompany: false,
    accountingOnly: false,
    useLocalImport: 2,
    packageMultiplier: 1,
    servers: [request.serverId],
    wpfCompanies: request.wpfCompanies && request.wpfCompanies.length > 0 ? request.wpfCompanies : null,
  }
  return axiosInstance.post('/config-api/company/create', payload)
}

export function useMutationCreateCompanyOnServer(serverId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: CreateCompanyOnServerRequest) => createCompanyOnServer(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['server-companies', serverId] })
      queryClient.invalidateQueries({ queryKey: ['config.companies'] })
    },
  })
}
